import type { Container, VisualElement } from "@judo/model-api";

/**
 * Find visual element by its @type property.
 */
export function findVisualElementByType<T extends VisualElement = VisualElement>(
	elements: VisualElement | VisualElement[],
	type: string
): T | undefined {
	const elementArray = Array.isArray(elements) ? elements : [elements];

	for (const element of elementArray) {
		if (element["@type"] === type) {
			return element as T;
		}
		// Recursively search children if this is a container
		if (hasChildren(element)) {
			const found = findVisualElementByType<T>(element.children, type);
			if (found) {
				return found;
			}
		}
	}

	return undefined;
}

/**
 * Find visual element by its xmi:id.
 */
export function findVisualElementById<T extends VisualElement = VisualElement>(
	elements: VisualElement | VisualElement[],
	id: string
): T | undefined {
	const elementArray = Array.isArray(elements) ? elements : [elements];

	for (const element of elementArray) {
		if (element["xmi:id"] === id) {
			return element as T;
		}
		// Recursively search children
		if (hasChildren(element)) {
			const found = findVisualElementById<T>(element.children, id);
			if (found) {
				return found;
			}
		}
	}

	return undefined;
}

/**
 * Find visual element by its sourceId.
 */
export function findVisualElementBySourceId<T extends VisualElement = VisualElement>(
	elements: VisualElement | VisualElement[],
	sourceId: string
): T | undefined {
	const elementArray = Array.isArray(elements) ? elements : [elements];

	for (const element of elementArray) {
		if (element.sourceId === sourceId) {
			return element as T;
		}
		// Recursively search children
		if (hasChildren(element)) {
			const found = findVisualElementBySourceId<T>(element.children, sourceId);
			if (found) {
				return found;
			}
		}
	}

	return undefined;
}

/**
 * Flatten visual element tree to array.
 */
export function flattenVisualElements(elements: VisualElement | VisualElement[]): VisualElement[] {
	const elementArray = Array.isArray(elements) ? elements : [elements];
	const result: VisualElement[] = [];

	for (const element of elementArray) {
		result.push(element);
		if (hasChildren(element)) {
			result.push(...flattenVisualElements(element.children));
		}
	}

	return result;
}

/**
 * Type guard to check if element has children.
 */
function hasChildren(element: VisualElement): element is Container {
	return "children" in element && Array.isArray((element as Container).children);
}
